//Import Mixpanel SDK
import mixpanel from 'mixpanel-browser';
import { useDidMount } from 'rooks';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelf } from '@/data/user';
import { useCompany } from '@/data/company';

const mixpanelToken = process.env.MIXPANEL_TOKEN;
const mixpanelEnabled = process.env.MIXPANEL_ENABLED;

export const useInitializeMixpanel = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  useDidMount(() => {
    if (mixpanelEnabled && mixpanelToken) {
      mixpanel.init(mixpanelToken, {
        autocapture: false,
        record_sessions_percent: 100,
        track_pageview: false
      });
    }
    setIsInitialized(true);
  });

  return { isInitialized };
};

export const useMixpanelUser = () => {
  const user = useSelf(false);
  const company = useCompany();
  const isInitializedRef = useRef(false);

  useDidMount(() => {
    if (mixpanelEnabled && mixpanelToken) {
      if (!isInitializedRef.current) {
        // First identify the user with mixpanel
        mixpanel.identify(user.data.uid);

        // Then set the user's properties
        mixpanel.people.set({
          // Default properties from mixpanel documentation
          $name: `${user.data.firstname} ${user.data.lastname}`,
          $email: user.data.email,

          // Custom properties that we want to track
          Company: company.data.company_name,
          Roles: user.data.roles
        });
        isInitializedRef.current = true;
      }
    }
  });
};

export function useMixpanelPageViews() {
  const location = useLocation();
  const lastPathRef = useRef(null);

  useEffect(() => {
    if (mixpanelEnabled && mixpanelToken) {
      const UUID_REGEX = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
      const rawPath = location.pathname;
      const normalizedPath = rawPath.replace(UUID_REGEX, '<<uuid>>');

      if (lastPathRef.current === normalizedPath) return;
      lastPathRef.current = normalizedPath;

      mixpanel.track('Page Viewed', {
        path: normalizedPath,
        raw_path: rawPath
      });
    }
  }, [location.pathname]);
}

export const useResetMixpanel = () => {
  const resetMixpanel = useCallback(() => {
    if (mixpanelEnabled && mixpanelToken) {
      mixpanel.reset();
    }
  }, []);
  return resetMixpanel;
};
